import React from 'react'
import { useDispatch } from 'react-redux'
import { logError } from '@core/Utils/logging/logging'
import { getStartupParameters } from '@core/Areas/KambiClient/config/startupParameters'
import customerSettings from '@core/Areas/KambiClient/config/customerSettings'
import widgetSettings from '@core/Areas/KambiClient/config/widgetSettings'
import { startListening } from '@core/Store/listenerMiddleware'
import { state$ as userAccountState$ } from '@core/State/UserAccount/userAccountDriver'
import {
  isUserResidentOfSA,
  isUserAccountStatePending,
  waitForWapi,
  toggleKambiVisibility,
  setInitialVisibilityState,
  kambiLog,
  handleKambiLogin,
  handleKambiLogout,
  isKambiSupportedBrowser,
} from '@core/Areas/KambiClient/helpers'
import { registerKambiCustomWidgets } from '@core/Areas/KambiClient/widgets/register'
import { queryClient } from '@core/Data/ReactQuery'
import { AppRoutes } from '@core/AppRoutes'
import { appHistory } from '@core/Utils/routing/history'
import { serverConfigQuery } from '@core/Data/ServerConfig/query'
import { setKambiClientVisibility, setUnsupportedBrowser } from '@core/Areas/KambiClient/Store'
import { BootstrapJurisdiction, handleScriptLoading } from './helpers/handleScriptLoading'
import { handleClientSubscriptions } from './helpers/handleClientSubscriptions'

let hasLoadedScript = false

type Props = {
  isDisabled?: boolean
}

export const useKambiClient = (props: Props): void => {
  const { isDisabled = false } = props
  const dispatch = useDispatch()

  const bootstrapJurisdictionRef = React.useRef<BootstrapJurisdiction>()
  const [isPendingReloadForJurisdiction, setIsPendingReloadForJurisdiction] = React.useState(false)

  // ==================================
  // Handle Pending Reload After SignUp
  // ==================================
  React.useEffect(() => {
    if (isDisabled) return
    if (!isPendingReloadForJurisdiction) return
    const dispose = appHistory.listen(() => {
      if (!isInSignUp()) location.reload()
    })
    return () => dispose()
  }, [isPendingReloadForJurisdiction, isDisabled])

  // ============
  // Load Scripts
  // ============
  React.useEffect(() => {
    if (isDisabled) return
    if (hasLoadedScript || bootstrapJurisdictionRef.current) return

    if (!isKambiSupportedBrowser()) {
      dispatch(setUnsupportedBrowser())
      return
    }

    hasLoadedScript = true

    queryClient.fetchQuery(serverConfigQuery).then(({ serverEnvironment }) => {
      window._kc = getStartupParameters(serverEnvironment)
      window.customerSettings = customerSettings
      window.widgetSettings = widgetSettings

      registerKambiCustomWidgets()

      handleScriptLoading()
        .then(bootstrapJurisdiction => {
          bootstrapJurisdictionRef.current = bootstrapJurisdiction
        })
        .catch(() => {
          logError('Error loading Kambi scripts', true)
        })
    })
  }, [dispatch, isDisabled])

  // ==================================
  // Register WAPI when widget is ready
  // ==================================
  React.useEffect(() => {
    if (isDisabled) return
    if (window.wapi) return

    waitForWapi().then(wapi => {
      // Set initial betslip state minimized + sets up subscription
      wapi.set(wapi.BETSLIP_MAXIMIZED, { maximized: false })
      wapi.request(wapi.USER_DATA)
      wapi.request(wapi.BETSLIP_OUTCOMES)

      setInitialVisibilityState(wapi)

      wapi.subscribe(response => {
        kambiLog('client event', response)
        handleClientSubscriptions(response)
      })
    })
  }, [isDisabled])

  // ================================================
  // Handle Authentication and Bootstrap Jurisdiction
  // ================================================
  React.useEffect(() => {
    if (isDisabled) return
    let shouldTryLogout: boolean

    const subscription = userAccountState$
      .skipWhile(isUserAccountStatePending)
      .map(userAccountState => ({
        accountNumber: userAccountState.accountNumber,
        isLoggedIn: userAccountState.isLoggedIn,
        residentialDetail: userAccountState.residentialDetail,
      }))
      .distinctUntilChanged(
        s => s,
        (a, b) => JSON.stringify(a) === JSON.stringify(b)
      )
      .subscribe(({ residentialDetail, isLoggedIn, accountNumber }) => {
        shouldTryLogout = !!isLoggedIn

        if (isLoggedIn && accountNumber && residentialDetail) {
          handleKambiLogin(accountNumber, residentialDetail)
        } else {
          handleKambiLogout()
        }

        if (!bootstrapJurisdictionRef.current) return

        const requiredBootstrap: BootstrapJurisdiction = isUserResidentOfSA(residentialDetail)
          ? 'SA'
          : 'WA'

        if (requiredBootstrap !== bootstrapJurisdictionRef.current) {
          if (isInSignUp()) {
            setIsPendingReloadForJurisdiction(true)
          } else {
            window.location.reload()
          }
        }
      })

    return () => {
      if (shouldTryLogout) handleKambiLogout()
      subscription.dispose()
    }
  }, [dispatch, isDisabled])

  // =======================
  // Visibility Subscription
  // =======================
  React.useEffect(() => {
    if (isDisabled) return
    const subscription = startListening({
      actionCreator: setKambiClientVisibility,
      effect: action => toggleKambiVisibility(!!action.payload),
    })
    return () => subscription()
  }, [isDisabled])
}

// =============
// Local Helpers
// =============

function isInSignUp(): boolean {
  return location.pathname.startsWith(AppRoutes.SignUp)
}
